<script setup lang="ts">
  import {
    ref,
    watch,
    defineProps,
    defineEmits,
    onMounted,
    onUnmounted,
    computed,
  } from 'vue';
  import IconAtom from '@/components/atoms/IconAtom.vue';
  import ButtonAtom from '@/components/atoms/ButtonAtom.vue';
  import InputAtom from '@/components/atoms/InputAtom.vue';
  import InputSelectAtom from '@/components/atoms/InputSelectAtom.vue';
  import AddNewTagAtom from '@/components/atoms/AddNewTagAtom.vue';
  import AddToCollectionAtom from '@/components/atoms/AddToCollectionAtom.vue';
  import {
    SelectOption,
    transformToSelectOptions,
  } from '@/shared/selectOption';
  import tagService from '@/services/tag/tag.service';
  import Collection from '@/models/collection/collection.model';
  import collectionService from '@/services/collection/collection.service';
  import { useStore } from 'vuex';

  const props = defineProps<{ resourceDetails: any }>();
  const emit = defineEmits(['updateResourceDetails', 'updatePreview']);
  const store = useStore();

  const localResourceDetails = ref({ 
    ...props.resourceDetails
  });
  const showAddNewTagModal = ref(false);
  const showAddToCollectionModal = ref(false);
  const coverImage = ref<string | null>(null);
  const audioFile = ref<string | null>(null);
  const uploadedImg = ref<HTMLInputElement | null>(null);
  const uploadedAudio = ref<HTMLInputElement | null>(null);
  const tagOptions = ref<SelectOption[]>([]);
  const collectionOptions = ref<Collection[]>([]);

  const actionCollectionsGetter = 'resource/getResourceCollections';
  const actionCollectionSet = 'resource/addCollection';
  const actionCollectionRemove = 'resource/removeCollection';
  const actionTagGetter = 'resource/getResourceTags';
  const actionTagSet = 'resource/addTag';
  const actionTagRemove = 'resource/removeTag';

  const audioPlayer = ref<HTMLAudioElement | null>(null);
  const playPauseBtn = ref<HTMLInputElement | null>(null);
  const audioPlayIcon = ref<HTMLInputElement | null>(null);
  const audioPauseIcon = ref<HTMLInputElement | null>(null);
  const progressBar = ref<HTMLInputElement | null>(null);
  const audioVolumeControl = ref<HTMLInputElement | null>(null);
  const audioMuteToggle = ref<HTMLInputElement | null>(null);
  const timeDisplay = ref<HTMLInputElement | null>(null);

  const selectedCollections = computed(
    () => store.getters[actionCollectionsGetter] || [],
  );

  onMounted(() => {
    fetchTags();
    fetchCollections();
  });

  watch(
    () => props.resourceDetails,
    (newValue) => {
      localResourceDetails.value = { ...newValue };
      coverImage.value = localResourceDetails.value.banner;
      audioFile.value = localResourceDetails.value.url_path;
    },
    { immediate: true, deep: true },
  );

  watch(
    localResourceDetails,
    () => {
      updateResourceDetails();
    },
    { deep: true },
  );

  const fetchTags = async () => {
    tagOptions.value = transformToSelectOptions(
      ((await tagService.getAllNoFilter()) as any).data,
      'tag_id',
      'tag_name',
    );
  };

  const fetchCollections = async () => {
    collectionOptions.value = (
      (await collectionService.getAllNoFilter()) as any
    ).data;
  };

  function handleNewTagAdded(newTag: string) {
    fetchTags();
    showAddNewTagModal.value = false;
  }

  function openAddNewTagModal() {
    showAddNewTagModal.value = true;
  }

  function openAddToCollectionModal() {
    showAddToCollectionModal.value = true;
  }

  function triggerImgUpload() {
    uploadedImg.value?.click();
  }

  function triggerAudioUpload() {
    uploadedAudio.value?.click();
  }

  function imgupload(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      coverImage.value = e.target?.result as string;
      updateResourceDetails();
    };
    reader.readAsDataURL(file);
    localResourceDetails.value.banner = file;
  }

  function audioChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = (e) => {
      audioFile.value = e.target?.result as string;
      updateResourceDetails();
    };
    reader.readAsDataURL(file);
    localResourceDetails.value.url_path = file;
  }

  function clearImage() {
    coverImage.value = null;
    if (uploadedImg.value) {
      uploadedImg.value.value = '';
      localResourceDetails.value.banner = '';
    }
    updateResourceDetails();
  }

  function clearAudioFile() {
    if (uploadedAudio.value) {
      uploadedAudio.value.value = '';
      localResourceDetails.value.url_path = '';
    }
    audioFile.value = null;
    updateResourceDetails();
  }

  function updateResourceDetails() {
    emit('updateResourceDetails', localResourceDetails);
    setAudioFileUrl();
  }

  function handleCollectionsUpdate(collections: string[]) {
    localResourceDetails.value.collections = collections;
    showAddToCollectionModal.value = false;
  }

  function handleCollectionRemove(collection: string) {
    store.dispatch(actionCollectionRemove, collection);
    localResourceDetails.value.collections = selectedCollections.value;

    if (selectedCollections.value.length === 0) {
      showAddToCollectionModal.value = false;
    }

    updateResourceDetails();
  }

  function setAudioFileUrl() {
    if (!localResourceDetails.value.url_path) {
      return null;
    }
    if (typeof localResourceDetails.value.url_path == 'object') {
      const fileURL = URL.createObjectURL(localResourceDetails.value.url_path);
      audioFile.value = fileURL;
    }
  }

  function addTagToSelection(newTag) {
    if (!localResourceDetails.value.tags) {
      localResourceDetails.value.tags = [];
    }

    if (localResourceDetails.value.tags.length < 3) {
      localResourceDetails.value.tags.push({
        id: newTag.tag_id,
        value: newTag.tag_name,
      });

      store.dispatch(actionTagSet, {
        id: newTag.tag_id,
        value: newTag.tag_name,
      });
    }
  }

  updateResourceDetails();

  const isPaused = ref(true);
  const isMuted = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const progress = ref(0);

  onMounted(() => {
    if (audioPlayer.value) {
      audioPlayer.value.controls = false;
      playPauseBtn.value?.addEventListener('click', togglePlayPause);
      audioPlayer.value?.addEventListener('loadedmetadata', updateDuration);
      audioPlayer.value?.addEventListener('timeupdate', updateTime);
      progressBar.value?.addEventListener('input', updateProgress);
      audioVolumeControl.value?.addEventListener('input', updateVolume);
      audioMuteToggle.value?.addEventListener('click', toggleMute);
      audioPlayer.value?.addEventListener('ended', resetPlayer);
      audioVolumeControl.value?.addEventListener('input', updateVolume);
    }
  });

  onUnmounted(() => {
    if (audioPlayer.value) {
      playPauseBtn.value?.removeEventListener('click', togglePlayPause);
      audioPlayer.value?.removeEventListener('loadedmetadata', updateDuration);
      audioPlayer.value?.removeEventListener('timeupdate', updateTime);
      progressBar.value?.removeEventListener('input', updateProgress);
      audioVolumeControl.value?.removeEventListener('input', updateVolume);
      audioMuteToggle.value?.removeEventListener('click', toggleMute);
      audioPlayer.value?.removeEventListener('ended', resetPlayer);
      audioVolumeControl.value?.removeEventListener('input', updateVolume);
    }
  });

  function togglePlayPause() {
    if (audioPlayer.value?.paused || audioPlayer.value?.ended) {
      audioPlayer.value?.play();
      isPaused.value = false;
    } else {
      audioPlayer.value?.pause();
      isPaused.value = true;
    }
    updatePlayPauseIcons();
  }

  function updatePlayPauseIcons() {
    if (isPaused.value) {
      audioPlayIcon.value?.style.setProperty('display', 'inline');
      audioPauseIcon.value?.style.setProperty('display', 'none');
    } else {
      audioPlayIcon.value?.style.setProperty('display', 'none');
      audioPauseIcon.value?.style.setProperty('display', 'inline');
    }
  }

  function updateDuration() {
    duration.value = audioPlayer.value?.duration || 0;
    timeDisplay.value!.textContent = `00:00 / ${formatTime(duration.value)}`;
  }

  function updateTime() {
    if (audioPlayer.value) {
      currentTime.value = audioPlayer.value.currentTime;
      progress.value =
        (audioPlayer.value.currentTime / audioPlayer.value.duration) * 100;
      timeDisplay.value!.textContent = `${formatTime(
        currentTime.value,
      )} / ${formatTime(duration.value)}`;
      progressBar.value!.value = progress.value.toString();
      progressBar.value?.style.setProperty(
        '--progress-percent',
        `${progress.value}%`,
      );
    }
  }

  function updateProgress() {
    if (audioPlayer.value && progressBar.value) {
      audioPlayer.value.currentTime =
        (parseFloat(progressBar.value.value) / 100) *
        audioPlayer.value.duration;
    }
  }

  const updateVolume = () => {
    if (audioPlayer.value && audioVolumeControl.value) {
      audioPlayer.value.volume =
        parseFloat(audioVolumeControl.value.value) / 100;
      audioVolumeControl.value.style.setProperty(
        '--volume-percent',
        `${audioVolumeControl.value.value}%`,
      );
      isMuted.value = audioPlayer.value.volume === 0;
      updateMuteIcons();
    }
  };

  function toggleMute() {
    if (audioPlayer.value) {
      audioPlayer.value.muted = !audioPlayer.value.muted;
      isMuted.value = audioPlayer.value.muted;
      updateMuteIcons();
    }
  }

  function updateMuteIcons() {
    const volumeIcon = document.getElementById('audio-volume-icon');
    const muteIcon = document.getElementById('audio-mute-icon');
    if (isMuted.value) {
      volumeIcon!.style.display = 'none';
      muteIcon!.style.display = 'inline';
    } else {
      volumeIcon!.style.display = 'inline';
      muteIcon!.style.display = 'none';
    }
  }

  function resetPlayer() {
    isPaused.value = true;
    audioPlayer.value!.currentTime = 0;
    timeDisplay.value!.textContent = `00:00 / ${formatTime(duration.value)}`;
    updatePlayPauseIcons();
  }

  function formatTime(time: number) {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(time % 60).toString();
    return `${minutes}:${seconds}`;
  }
</script>

<template>
  <div class="audio-editor-molecule">
    <div class="cover-img">
      <h3 class="editor-title">Add new audio details</h3>
      <ButtonAtom
        class="btn btn-secondary coverBtn"
        :style="{
          backgroundImage: coverImage ? `url(${coverImage})` : '',
          border: coverImage ? 'none' : '1.5px dashed #ccc',
        }"
        @click="triggerImgUpload"
      >
        <IconAtom v-if="!coverImage" icon="cameraIcon" />
        <p v-if="!coverImage">Cover</p>

        <button
          v-if="coverImage"
          class="clear-image-btn"
          @click.stop="clearImage"
        >
          X
        </button>
      </ButtonAtom>
      <input
        type="file"
        ref="uploadedImg"
        accept="image/*"
        @change="imgupload"
        style="display: none"
      />
    </div>

    <div class="cover-audio">
      <label for="title">Media</label>
      <ButtonAtom
        class="btn btn-secondary audioCoverButton"
        :style="{}"
        @click="triggerAudioUpload"
        v-if="!audioFile"
      >
        <IconAtom icon="audioIcon" />
        <p>Select an audio file</p>
      </ButtonAtom>
      <!-- MY AUDIO START -->
      <div v-if="audioFile" class="my-audio">
        <audio ref="audioPlayer">
          <source :src="audioFile" type="audio/mpeg" />
          <source :src="audioFile" type="audio/ogg" />
          Your browser does not support the audio element.
        </audio>
        <div class="audio-controls">
          <button ref="playPauseBtn" id="audio-play-pause">
            <svg ref="audioPlayIcon" id="audio-play-icon" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"></path>
            </svg>
            <svg
              ref="audioPauseIcon"
              id="audio-pause-icon"
              viewBox="0 0 24 24"
              style="display: none"
            >
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"></path>
            </svg>
          </button>
          <span ref="timeDisplay" id="time-display">00:00 / 00:00</span>
          <input
            type="range"
            ref="progressBar"
            id="audio-progress"
            value="0"
            max="100"
          />
          <button ref="audioMuteToggle" id="audio-mute-toggle">
            <IconAtom icon="volumeIcon" id="audio-volume-icon" color="black" />
            <IconAtom icon="muteIcon" id="audio-mute-icon" color="black" />
          </button>
          <input
            type="range"
            ref="audioVolumeControl"
            id="audio-volume-control"
            value="100"
            max="100"
          />
          <button
            v-if="audioFile"
            class="clear-audio-button"
            @click.stop="clearAudioFile"
          >
            X
          </button>
        </div>
      </div>
      <!-- MY AUDIO END -->
      <input
        type="file"
        ref="uploadedAudio"
        accept="audio/*"
        @change="audioChange"
        style="display: none"
      />
    </div>

    <div class="title">
      <label for="title">Title</label>
      <InputAtom
        id="title"
        v-model="localResourceDetails.resource_name"
        placeholder="Name"
        class="input"
      />
    </div>

    <div class="tags">
      <label for="tags">Tags (maximum 3)</label>
      <InputSelectAtom
        id="tags"
        placeholder="Add tags"
        v-model="localResourceDetails.tags"
        :options="tagOptions"
        @addNew="openAddNewTagModal"
        :tagsGetter="actionTagGetter"
        :tagSet="actionTagSet"
        :tagRemove="actionTagRemove"
      />
      <AddNewTagAtom
        :show="showAddNewTagModal"
        @update:show="showAddNewTagModal = $event"
        @newTagAdded="handleNewTagAdded"
        @addTagToSelection="addTagToSelection"
      />
    </div>

    <div class="premium-checkbox">
      <label class="checkbox-label">
        <input
          type="checkbox"
          class="checkbox-input"
          v-model="localResourceDetails.is_premium"
        />
        <span class="custom-checkbox"></span>
        <span class="checkbox-label-text">Premium</span>
      </label>
    </div>

    <div class="collection">
      <label for="collection">Collection</label>
      <div v-if="selectedCollections.length > 0">
        <div
          v-for="collection in selectedCollections"
          :key="collection"
          class="collection-item"
        >
          <div class="name-and-img">
            <img
              v-if="collection.banner"
              :src="collection.banner"
              alt="Collection Image"
              class="collection-icon"
            />

            {{ collection.collection_name }}
          </div>
          <IconAtom
            icon="deleteIcon"
            @click="() => handleCollectionRemove(collection)"
            class="pointer"
          />
        </div>
      </div>
      <ButtonAtom
        class="btn btn-secondary collectionBtn"
        @click="openAddToCollectionModal"
      >
        + Add to a collection
      </ButtonAtom>
      <AddToCollectionAtom
        :show="showAddToCollectionModal"
        :collections="collectionOptions"
        @update:show="showAddToCollectionModal = $event"
        @updateCollections="handleCollectionsUpdate"
        :actionCollectionsGetter="actionCollectionsGetter"
        :actionCollectionSet="actionCollectionSet"
        :actionCollectionRemove="actionCollectionRemove"
      />
    </div>

    <div class="caption">
      <label for="caption">Captions</label>
      <InputAtom
        id="caption"
        placeholder="Captions are auto-generated"
        class="input"
      />
    </div>
  </div>
</template>

<style scoped>
  .audio-editor-molecule {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    height: 100%;
    margin: auto;
  }

  .cover-img {
    display: flex;
    flex-direction: column;
  }

  .btn {
    position: relative;
    display: flex;
    flex-direction: column;
    margin: auto;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background-size: cover;
    background-position: center;
  }

  .btn.coverBtn {
    height: 100px;
    width: 100px;
  }

  .clear-image-btn {
    position: absolute;
    top: -10px;
    right: -10px;
    border: none;
    border-radius: 50%;
    width: 28px;
    height: 28px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
  }

  .cover-audio {
    display: flex;
    flex-direction: column;
  }

  .btn.audioCoverButton {
    height: 100px;
    width: 100%;
    border: 1.5px dashed #ccc;
  }

  .clear-audio-button {
    position: absolute;
    right: -40px;
    top: 50%;
    transform: translateY(-50%);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: balck;
    background-color: #f1f1f1;
  }

  .btn.collectionBtn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 50px;
    margin-top: 10px;
  }

  .editor-title {
    display: block;
    margin: 16px 0;
    font-weight: bold;
    text-align: left;
  }

  label {
    display: block;
    margin: 16px 0;
    font-weight: bold;
    text-align: left;
  }

  .title,
  .tags,
  .collection,
  .caption {
    display: flex;
    flex-direction: column;
  }

  .collection-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin-top: 5px;
    border-radius: 5px;
    background-color: var(--color-gray-50);
  }

  .collection-item .pointer {
    cursor: pointer;
  }

  .collection-item {
    display: flex;
    align-items: center;
  }

  .collection-icon {
    height: 40px;
    width: auto;
    margin-right: 10px;
    border-radius: 6px;
  }

  .name-and-img {
    display: flex;
    align-items: center;
  }

  .my-audio {
    position: relative;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }

  .audio-controls {
    display: flex;
    align-items: center;
    background-color: #f1f1f1;
    padding: 12px 24px;
    border-radius: 16px;
    width: 95%;
    position: relative;
  }

  .audio-controls button {
    /* background: none; */
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .audio-controls button svg {
    width: 24px;
    height: 24px;
  }

  #time-display {
    font-size: 12px;
    color: #000;
    min-width: 100px;
    text-align: center;
  }

  #audio-progress {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    background: #cecfd2;
    cursor: pointer;
    margin: 0 10px;
    height: 5px;
    border-radius: 5px;
    position: relative;
    overflow: visible;
  }

  #audio-volume-control {
    -webkit-appearance: none;
    appearance: none;
    width: 50%;
    background: #cecfd2;
    cursor: pointer;
    margin: 0 10px;
    height: 5px;
    border-radius: 5px;
    position: relative;
    overflow: visible;
  }

  .my-audio:hover #audio-volume-control {
    visibility: visible;
  }

  .my-audio:not(:hover) #audio-volume-control {
    display: none;
  }

  #audio-progress::-webkit-slider-runnable-track,
  #audio-volume-control::-webkit-slider-runnable-track {
    height: 5px;
    background: transparent;
  }

  #audio-progress::-webkit-slider-thumb,
  #audio-volume-control::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 15px;
    height: 15px;
    background: white;
    border-radius: 50%;
    cursor: pointer;
    margin-top: -5px;
    position: relative;
    z-index: 2;
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
  }

  #audio-progress:before,
  #audio-volume-control:before {
    content: '';
    position: absolute;
    height: 100%;
    top: 0;
    left: 0;
    background: linear-gradient(
      to right,
      #fda99a 45%,
      #f3a9df 60%,
      #86cafe 100%
    );
    border-radius: 5px;
    z-index: 1;
  }

  #audio-progress:before {
    width: var(--progress-percent, 0%);
  }

  #audio-volume-control:before {
    width: var(--volume-percent, 100%);
  }

  #audio-progress:focus,
  #audio-volume-control:focus {
    outline: none;
  }

  .audio-controls #audio-volume-icon {
    width: 24px;
    height: 24px;
    stroke: #000;
    fill: none;
  }

  audio[controls] {
    display: none; /* Hide the native audio controls */
  }

  .premium-checkbox {
    margin-top: 8px;
  }

  .checkbox-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    gap: 8px;
  }

  .checkbox-input {
    display: none;
  }

  .custom-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #ccc;
    border-radius: 4px;
    position: relative;
    transition: all 0.2s;
  }

  .checkbox-input:checked + .custom-checkbox {
    background-color: black;
    border-color: black;
  }

  .checkbox-input:checked + .custom-checkbox::after {
    content: '';
    position: absolute;
    left: 4px;
    top: 1px;
    width: 6px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  .checkbox-label-text {
    font-size: 14px;
    font-weight: normal;
    color: #333;
  }
</style>
